(() => {
  if (customElements.get('pyp-photo-crop')) return;
  const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
  const checkedPhotos = new WeakMap();
  const photoError = () => new Error('This photo is too large to edit on this device. Please choose a smaller JPG or PNG.');
  function photoDimensions(bytes) {
    const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
    const text = (offset, length) => String.fromCharCode(...bytes.subarray(offset, offset + length));
    if ([137, 80, 78, 71, 13, 10, 26, 10].every((value, index) => bytes[index] === value)) {
      if (bytes.length < 33 || view.getUint32(8) !== 13 || text(12, 4) !== 'IHDR') throw photoError();
      return { width: view.getUint32(16), height: view.getUint32(20) };
    }
    if (bytes[0] === 0xff && bytes[1] === 0xd8) {
      const frames = new Set([0xc0, 0xc1, 0xc2, 0xc3, 0xc5, 0xc6, 0xc7, 0xc9, 0xca, 0xcb, 0xcd, 0xce, 0xcf]);
      let offset = 2, segments = 0;
      while (offset < bytes.length && segments++ < 1024) {
        if (bytes[offset++] !== 0xff) throw photoError();
        while (bytes[offset] === 0xff) offset += 1;
        const marker = bytes[offset++];
        if (marker === 0xda || marker === 0xd9 || marker === undefined) throw photoError();
        if (marker === 0x01 || (marker >= 0xd0 && marker <= 0xd7)) continue;
        if (offset + 2 > bytes.length) throw photoError();
        const length = view.getUint16(offset);
        if (length < 2 || offset + length > bytes.length) throw photoError();
        if (frames.has(marker)) {
          if (length < 8) throw photoError();
          return { width: view.getUint16(offset + 5), height: view.getUint16(offset + 3) };
        }
        offset += length;
      }
      throw photoError();
    }
    if (text(0, 4) === 'RIFF' && text(8, 4) === 'WEBP') {
      if (bytes.length < 20) throw photoError();
      const end = view.getUint32(4, true) + 8;
      if (end > bytes.length || end < 20) throw photoError();
      const uint24 = offset => bytes[offset] | bytes[offset + 1] << 8 | bytes[offset + 2] << 16;
      let offset = 12, chunks = 0;
      while (offset + 8 <= end && chunks++ < 1024) {
        const type = text(offset, 4), length = view.getUint32(offset + 4, true), start = offset + 8;
        if (start + length > end) throw photoError();
        if (type === 'VP8X') {
          if (length !== 10) throw photoError();
          return { width: uint24(start + 4) + 1, height: uint24(start + 7) + 1 };
        }
        if (type === 'VP8L') {
          if (length < 5 || bytes[start] !== 0x2f) throw photoError();
          const bits = view.getUint32(start + 1, true);
          return { width: (bits & 0x3fff) + 1, height: ((bits >>> 14) & 0x3fff) + 1 };
        }
        if (type === 'VP8 ') {
          if (length < 10 || bytes[start] & 1 || bytes[start + 3] !== 0x9d || bytes[start + 4] !== 1 || bytes[start + 5] !== 0x2a) throw photoError();
          return { width: view.getUint16(start + 6, true) & 0x3fff, height: view.getUint16(start + 8, true) & 0x3fff };
        }
        offset = start + length + (length & 1);
      }
      throw photoError();
    }
    // HEIC/HEIF containers still require the browser's native decoder. Their
    // primary canvas may be assembled from tiles; an arbitrary ispe is not it.
    if (bytes.length >= 16 && text(4, 4) === 'ftyp') return null;
    throw photoError();
  }
  class PypPhotoCrop extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.dialog = this.querySelector('dialog');
      this.canvas = this.querySelector('canvas');
      this.zoomInput = this.querySelector('[data-crop-zoom]');
      this.confirm = this.querySelector('[data-crop-confirm]');
      this.revision = 0;
      this.compactMedia = matchMedia('(min-width: 990px) and (max-width: 1364px) and (max-height: 700px) and (hover: hover) and (pointer: fine)');
      this.compactMedia.addEventListener('change', () => this.syncCompactLayout(), { signal });
      this.syncCompactLayout();
      this.compactObserver = new ResizeObserver(() => this.fitCompactCanvas());
      this.compactObserver.observe(this.querySelector('.pyp-crop__stage'));
      window.addEventListener('resize', () => {
        this.syncCompactLayout();
        this.fitCompactCanvas();
        if (this.dialog.open && this.warningVisible) this.setWarning(true, true);
      }, { signal });
      // Return focus according to the latest input, including mixed mouse/keyboard use.
      this.dialog.addEventListener('keydown', () => { this.keyboardOpen = true; }, { signal });
      this.dialog.addEventListener('pointerdown', () => { this.keyboardOpen = false; }, { signal });
      this.querySelectorAll('[data-crop-cancel]').forEach(button => button.addEventListener('click', () => this.cancelWithFeedback(button), { signal }));
      this.dialog.addEventListener('cancel', event => { event.preventDefault(); this.cancelPending(); }, { signal });
      this.dialog.addEventListener('close', () => {
        // A queued native close event must not cancel a newer open/decode.
        if (this.dialog.open) return;
        this.style.removeProperty('overflow');
        this.unlockScroll();
        this.releasePhoto();
        this.dispatchEvent(new CustomEvent('pyp:crop-closed', { bubbles: true }));
        if (this.keyboardOpen) this.opener?.focus({ preventScroll: true });
        else if (document.activeElement === this.opener) this.opener.blur();
      }, { signal });
      this.zoomInput.addEventListener('input', () => { this.stopZoom(); this.zoom = Number(this.zoomInput.value); this.draw(); }, { signal });
      this.querySelector('[data-crop-zoom-out]').addEventListener('click', () => this.changeZoom(-.1), { signal });
      this.querySelector('[data-crop-zoom-in]').addEventListener('click', () => this.changeZoom(.1), { signal });
      this.querySelector('[data-crop-reset]').addEventListener('click', () => { this.turn = 0; this.reset(); }, { signal });
      this.querySelector('[data-crop-rotate]').addEventListener('click', () => { this.turn = (this.turn + 1) % 4; this.reset(); }, { signal });
      this.confirm.addEventListener('click', () => this.exportCrop(), { signal });
      this.canvas.addEventListener('pointerdown', event => {
        if (this.drag || this.confirm.disabled) return;
        this.stopZoom();
        this.drag = { id: event.pointerId, x: event.clientX, y: event.clientY };
        this.canvas.setPointerCapture(event.pointerId);
      }, { signal });
      this.canvas.addEventListener('pointermove', event => {
        if (this.drag?.id !== event.pointerId) return;
        const rect = this.canvas.getBoundingClientRect();
        const crop = this.cropRect();
        this.cx -= (event.clientX - this.drag.x) / rect.width * crop.width;
        this.cy -= (event.clientY - this.drag.y) / rect.height * crop.height;
        this.drag.x = event.clientX; this.drag.y = event.clientY;
        this.draw();
      }, { signal });
      ['pointerup', 'pointercancel', 'lostpointercapture'].forEach(type => this.canvas.addEventListener(type, () => { this.drag = null; }, { signal }));
      this.canvas.addEventListener('keydown', event => {
        if (this.confirm.disabled) return;
        const directions = { ArrowLeft: [-1, 0], ArrowRight: [1, 0], ArrowUp: [0, -1], ArrowDown: [0, 1] };
        const delta = directions[event.key];
        if (!delta) return;
        event.preventDefault();
        this.stopZoom();
        const crop = this.cropRect();
        this.cx -= delta[0] * crop.width * .025;
        this.cy -= delta[1] * crop.height * .025;
        this.draw();
      }, { signal });
    }

    // Only rearrange existing nodes in the opted-in viewport. Preserve listeners and native order elsewhere.
    syncCompactLayout() {
      const compact = this.compactMedia.matches;
      if (compact === Boolean(this.compactPanel)) return;
      const surface = this.querySelector('.pyp-crop__surface');
      const body = this.querySelector('.pyp-crop__body');
      const header = this.querySelector('.pyp-crop__header');
      const controls = this.querySelector('.pyp-crop__controls');
      const footer = this.querySelector('.pyp-crop__footer');
      const focused = this.contains(document.activeElement) ? document.activeElement : null;
      this.toggleAttribute('data-compact-crop', compact);
      if (compact) {
        this.compactPanel = document.createElement('div');
        this.compactPanel.className = 'pyp-crop__panel';
        this.compactPanel.append(header, controls, footer);
        surface.append(this.compactPanel);
      } else {
        surface.insertBefore(header, body);
        body.append(controls);
        surface.append(footer);
        this.compactPanel.remove();
        this.compactPanel = null;
        this.canvas.style.removeProperty('width');
        this.canvas.style.removeProperty('height');
      }
      if (focused && document.activeElement !== focused) focused.focus({ preventScroll: true });
      if (this.image && this.dialog.open) {
        this.draw();
        this.setWarning(this.warningVisible, true);
        this.fitCompactCanvas();
      }
    }

    fitCompactCanvas() {
      if (!this.compactPanel || !this.image || !this.dialog.open) return;
      const stage = this.querySelector('.pyp-crop__stage');
      const style = getComputedStyle(stage);
      const width = stage.clientWidth - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight);
      const height = stage.clientHeight - parseFloat(style.paddingTop) - parseFloat(style.paddingBottom);
      const ratio = this.size.width / this.size.height;
      const fitHeight = Math.max(1, Math.min(height, width / ratio));
      this.canvas.style.width = `${fitHeight * ratio}px`;
      this.canvas.style.height = `${fitHeight}px`;
    }

    async inspectPhoto(file) {
      if (checkedPhotos.has(file)) return checkedPhotos.get(file);
      if (!file?.size || file.size > 20971520) throw photoError();
      // At most the existing 20 MB file limit, before any browser image decode.
      // Read the bounded file so large JPEG metadata cannot hide its size header.
      const pending = file.arrayBuffer().then(buffer => {
        const dimensions = photoDimensions(new Uint8Array(buffer));
        if (dimensions && (!dimensions.width || !dimensions.height || dimensions.width * dimensions.height > 80000000)) throw photoError();
        return dimensions;
      });
      checkedPhotos.set(file, pending);
      try { return await pending; }
      catch (error) { checkedPhotos.delete(file); throw error; }
    }

    async openPhoto(file, size, opener, previousCrop, keyboard = false) {
      this.stopZoom();
      this.warningMotion?.cancel();
      this.warningVisible = undefined;
      const revision = ++this.revision;
      await this.inspectPhoto(file);
      if (revision !== this.revision || !this.isConnected) return false;
      const url = URL.createObjectURL(file);
      const image = new Image();
      try {
        image.src = url;
        await image.decode();
        if (revision !== this.revision || !this.isConnected) return false;
        if (!image.naturalWidth || !image.naturalHeight || image.naturalWidth * image.naturalHeight > 80000000) {
          throw new Error('This photo is too large to edit on this device. Please choose a smaller JPG or PNG.');
        }
        this.image = image;
        this.source = file;
        this.size = size;
        this.opener = opener;
        this.keyboardOpen = keyboard;
        this.turn = 0;
        this.canvas.width = Math.round(600 * size.width / size.height);
        this.canvas.height = 600;
        this.canvas.style.setProperty('--crop-ratio', String(size.width / size.height));
        this.querySelector('[data-crop-size]').textContent = `${size.width} × ${size.height} cm`;
        this.querySelector('[data-crop-error]').hidden = true;
        this.setExporting(false);
        this.confirm.textContent = 'Use this photo';
        this.reset();
        if (previousCrop && Number.isFinite(previousCrop.width) && previousCrop.width > 0) {
          this.turn = Math.round(previousCrop.rotation / 90) % 4;
          const bounds = this.dimensions();
          this.zoom = clamp(Math.min(bounds.width, bounds.height * size.width / size.height) / previousCrop.width, 1, 3);
          this.zoomInput.value = String(this.zoom);
          this.cx = previousCrop.x + previousCrop.width / 2;
          this.cy = previousCrop.y + previousCrop.height / 2;
          this.draw();
        }
        this.lockScroll();
        window.MyArtModalMotion ? window.MyArtModalMotion.open(this.dialog) : this.dialog.showModal();
        // A closed native dialog has no layout: measure the initial warning only once visible.
        this.setWarning(Boolean(this.warningVisible), true);
        this.fitCompactCanvas();
        this.dialog.scrollTop = 0;
        this.querySelector('.pyp-crop__body').scrollTop = 0;
        const focusTarget = keyboard ? this.querySelector('[data-crop-cancel]') : this.querySelector('.pyp-crop__surface');
        focusTarget.focus({ preventScroll: true });
        return true;
      } finally { URL.revokeObjectURL(url); }
    }

    dimensions() {
      return this.turn % 2 ? { width: this.image.naturalHeight, height: this.image.naturalWidth }
        : { width: this.image.naturalWidth, height: this.image.naturalHeight };
    }

    reset() {
      this.stopZoom();
      const { width, height } = this.dimensions();
      this.zoom = 1; this.zoomInput.value = '1';
      this.cx = width / 2; this.cy = height / 2;
      this.draw();
    }

    stopZoom(settle = false) {
      if (this.zoomFrame != null) cancelAnimationFrame(this.zoomFrame);
      this.zoomFrame = null;
      if (settle && this.zoomTarget != null) { this.zoom = this.zoomTarget; this.draw(); }
      this.zoomTarget = null;
    }

    changeZoom(delta) {
      if (this.confirm.disabled || !this.image) return;
      const target = clamp((this.zoomTarget ?? this.zoom) + delta, 1, 3);
      this.stopZoom();
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        this.zoom = target; this.draw(); return;
      }
      const from = this.zoom;
      this.zoomTarget = target;
      let started;
      const step = now => {
        started ??= now;
        const progress = Math.min(1, (now - started) / 180);
        this.zoom = from + (target - from) * (1 - Math.pow(1 - progress, 3));
        this.draw();
        if (progress < 1) this.zoomFrame = requestAnimationFrame(step);
        else { this.zoomFrame = null; this.zoomTarget = null; }
      };
      this.zoomFrame = requestAnimationFrame(step);
    }

    cropRect() {
      const bounds = this.dimensions();
      const ratio = this.size.width / this.size.height;
      const width = Math.min(bounds.width, bounds.height * ratio) / this.zoom;
      const height = width / ratio;
      this.cx = clamp(this.cx, width / 2, bounds.width - width / 2);
      this.cy = clamp(this.cy, height / 2, bounds.height - height / 2);
      return { x: this.cx - width / 2, y: this.cy - height / 2, width, height };
    }

    paint(canvas) {
      const context = canvas.getContext('2d');
      if (!context) throw new Error('Photo editing is not available on this device. Please try a newer browser.');
      const crop = this.cropRect();
      const bounds = this.dimensions();
      context.save();
      context.fillStyle = '#fff'; context.fillRect(0, 0, canvas.width, canvas.height);
      context.scale(canvas.width / crop.width, canvas.height / crop.height);
      context.translate(-crop.x + bounds.width / 2, -crop.y + bounds.height / 2);
      context.rotate(this.turn * Math.PI / 2);
      context.drawImage(this.image, -this.image.naturalWidth / 2, -this.image.naturalHeight / 2);
      context.restore();
    }

    draw() {
      this.zoomInput.value = String(this.zoom);
      this.zoomInput.style.setProperty('--pyp-zoom-fill', `${clamp((this.zoom - 1) / 2, 0, 1) * 100}%`);
      this.paint(this.canvas);
      const crop = this.cropRect();
      const warning = this.querySelector('[data-crop-warning]');
      this.setWarning(Math.min(crop.width, crop.height) < 800);
    }

    setWarning(visible, resized = false) {
      // Keep the approved complete message stable while zooming in the compact editor.
      visible = this.compactMedia.matches || visible;
      const warning = this.querySelector('[data-crop-warning]');
      const slot = warning.parentElement;
      if (this.warningVisible === visible && !resized) return;
      this.warningVisible = visible;
      const from = slot.getBoundingClientRect().height;
      this.warningMotion?.cancel();
      warning.hidden = false;
      warning.textContent = 'This crop may look a little soft. Zoom out or choose a larger photo for clearer details.';
      const to = visible ? warning.getBoundingClientRect().height + 7 : 0;
      slot.style.height = `${to}px`;
      slot.setAttribute('aria-hidden', String(!visible));
      if (resized || !this.dialog.open || matchMedia('(prefers-reduced-motion: reduce)').matches || !slot.animate) return;
      const motion = slot.animate([{height: `${from}px`, opacity: from ? 1 : 0}, {height: `${to}px`, opacity: visible ? 1 : 0}], {duration: 240, easing: 'ease-in-out'});
      this.warningMotion = motion;
      motion.onfinish = () => { if (this.warningMotion === motion) this.warningMotion = null; };
    }

    async cancelWithFeedback(button) {
      if (button.disabled) return;
      const revision = ++this.revision;
      this.stopZoom();
      button.setAttribute('aria-busy', 'true');
      button.disabled = true;
      await new Promise(resolve => requestAnimationFrame(() => requestAnimationFrame(resolve)));
      if (revision === this.revision) this.cancelPending();
      button.removeAttribute('aria-busy');
      button.disabled = false;
    }

    async exportCrop() {
      if (this.confirm.disabled) return;
      // Confirm the requested final zoom even if the last +/- animation is still running.
      this.stopZoom(true);
      const revision = this.revision;
      this.setExporting(true);
      // The existing accessible label stays stable; CSS shows only the spinner.
      try {
        // Paint the acknowledged press before synchronous canvas work.
        await new Promise(resolve => requestAnimationFrame(() => requestAnimationFrame(resolve)));
        if (revision !== this.revision || !this.dialog.open) return;
        const crop = this.cropRect();
        const metadata = { ...crop, rotation: this.turn * 90, orientation: this.size.orientation || 'vertical', version: 2 };
        const source = this.source;
        const size = { ...this.size };
        const canvas = document.createElement('canvas');
        const scale = Math.min(1, 4096 / Math.max(crop.width, crop.height));
        canvas.width = Math.max(1, Math.round(crop.width * scale));
        canvas.height = Math.max(1, Math.round(crop.height * scale));
        this.paint(canvas);
        const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/jpeg', .95));
        canvas.width = 1; canvas.height = 1;
        if (revision !== this.revision || !this.dialog.open) return;
        if (!blob?.size) throw new Error('We could not prepare this photo. Please try another photo.');
        if (blob.size > 20971520) throw new Error('This crop is too large to upload. Please choose a smaller photo.');
        const file = new File([blob], `painting-${size.width}x${size.height}.jpg`, { type: 'image/jpeg' });
        this.dispatchEvent(new CustomEvent('pyp:crop-confirmed', { bubbles: true, detail: {
          file, original: source, size, crop: metadata
        } }));
        this.cancelPending();
      } catch (error) {
        if (revision !== this.revision || !this.dialog.open) return;
        const message = this.querySelector('[data-crop-error]');
        message.textContent = error.message; message.hidden = false;
      } finally {
        if (revision === this.revision) {
          this.setExporting(false); this.confirm.textContent = 'Use this photo';
        }
      }
    }

    setExporting(pending) {
      this.confirm.setAttribute('aria-busy', String(pending));
      this.drag = null;
      this.querySelectorAll('button:not([data-crop-cancel]), input').forEach(control => { control.disabled = pending; });
      this.canvas.setAttribute('aria-disabled', String(pending));
    }

    cancelPending(immediate = false) {
      this.warningMotion?.cancel(); this.stopZoom(); this.revision += 1;
      if (!this.dialog?.open) { this.releasePhoto(); return; }
      if (immediate || !window.MyArtModalMotion) { window.MyArtModalMotion?.cancel(this.dialog); this.dialog.close(); }
      else window.MyArtModalMotion.close(this.dialog);
    }
    releasePhoto() {
      // A closed editor needs no full-resolution decode or original-file reference.
      // The upload component keeps the selected original for an explicit re-edit.
      this.image = null; this.source = null; this.drag = null;
      if (this.canvas) { this.canvas.width = 1; this.canvas.height = 1; }
    }
    lockScroll() {
      if (this.scrollLock) return;
      const html = document.documentElement;
      const body = document.body;
      const viewportGutter = Math.max(0, window.innerWidth - body.getBoundingClientRect().width);
      const keys = ['position', 'top', 'left', 'right', 'width', 'overflow'];
      this.scrollLock = { overflow: html.style.overflow, scrollY: window.scrollY, body: Object.fromEntries(keys.map(key => [key, body.style[key]])) };
      html.style.overflow = 'hidden';
      body.style.width = `calc(100% - ${viewportGutter}px)`;
      if (!window.matchMedia('(min-width: 750px)').matches) {
        Object.assign(body.style, { position: 'fixed', top: `-${this.scrollLock.scrollY}px`, left: '0', right: '0', overflow: 'hidden' });
      }
    }
    unlockScroll() {
      if (!this.scrollLock) return;
      const state = this.scrollLock;
      document.documentElement.style.overflow = state.overflow;
      if (state.body) { Object.assign(document.body.style, state.body); window.scrollTo(0, state.scrollY); }
      this.scrollLock = null;
    }
    disconnectedCallback() {
      this.compactObserver?.disconnect(); this.compactObserver = null;
      this.cancelPending(true); this.unlockScroll(); this.controller?.abort(); this.image = null; this.source = null; this.controller = null;
    }
  }
  customElements.define('pyp-photo-crop', PypPhotoCrop);
})();
